import { inject, Injectable } from '@angular/core'
import { ExternalOrganizationApiClientFactory } from '@ftr/api-external-organization'
import { ApiClient, ApiClientFactory, serializeHttpParams } from '@ftr/api-shared'
import {
  CreateOrganizationRequest,
  InviteOrganizationUsersRequest,
  ListOrganizationUsersQuery,
  ListOrganizationUsersResponse,
  Organization,
  OrganizationInvite,
  OrganizationUser,
  UpdateOrganizationUserRoleBody,
} from '@ftr/contracts/api/organizations'
import { Region } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { OrganizationData, UpdateOrganizationBody } from '@ftr/external-organization-contracts'
import { ApiResult } from '@ftr/foundation'

@Injectable({ providedIn: 'root' })
export class OrganizationsService {
  private readonly userApiClient: ApiClient = inject(ApiClientFactory).build('/user')
  private readonly organizationsApiClient: ApiClient = inject(ApiClientFactory).build('/organizations')
  private readonly externalOrganizationApiClientFactory = inject(ExternalOrganizationApiClientFactory)

  /**
   * Creates an organization for the signed in user, who becomes its first admin. The region it
   * lives in is derived from `countryCode` by the API.
   */
  create(body: CreateOrganizationRequest): ApiResult<Organization> {
    return this.organizationsApiClient.post<Organization>({ body })
  }

  update(organizationId: Uuid, region: Region, body: UpdateOrganizationBody): ApiResult<OrganizationData> {
    return this.externalOrganizationApiClientFactory
      .buildForRegion('/organizations', region)
      .patch<OrganizationData, UpdateOrganizationBody>({
        path: `/${organizationId}`,
        body,
      })
  }

  fetchForCurrentUser(): ApiResult<Organization[]> {
    return this.userApiClient.get<Organization[]>({ path: '/organizations' })
  }

  listUsers(organizationId: Uuid, query: ListOrganizationUsersQuery): ApiResult<ListOrganizationUsersResponse> {
    return this.organizationsApiClient.get<ListOrganizationUsersResponse>({
      path: `/${organizationId}/users`,
      params: serializeHttpParams(query),
    })
  }

  getUser(organizationId: Uuid, userId: Uuid): ApiResult<OrganizationUser> {
    return this.organizationsApiClient.get<OrganizationUser>({
      path: `/${organizationId}/users/${userId}`,
    })
  }

  updateUserRole(
    organizationId: Uuid,
    userId: Uuid,
    body: UpdateOrganizationUserRoleBody,
  ): ApiResult<OrganizationUser> {
    return this.organizationsApiClient.patch<OrganizationUser, UpdateOrganizationUserRoleBody>({
      path: `/${organizationId}/users/${userId}/update-role`,
      body,
    })
  }

  removeUser(organizationId: Uuid, userId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.delete<undefined>({ path: `/${organizationId}/users/${userId}` })
  }

  acceptInvite(organizationId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.put<undefined>({
      path: `/${organizationId}/accept-invite`,
    })
  }

  declineInvite(organizationId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.delete<undefined>({ path: `/${organizationId}/decline-invite` })
  }

  inviteUsers(body: InviteOrganizationUsersRequest): ApiResult<OrganizationInvite[]> {
    return this.organizationsApiClient.post<OrganizationInvite[]>({
      path: '/invite',
      body,
    })
  }

  resendInvite(organizationId: Uuid, userId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.put<undefined>({
      path: `/${organizationId}/users/${userId}/resend-invite`,
    })
  }

  /*
  Generates the accept-invite URL for a given organization, for use in the copy invite link command
   */
  getInviteUrl(organizationId: Uuid): string {
    return `${this.organizationsApiClient.baseUrl}/${organizationId}/accept-invite`
  }
}
